/**
 * iter743 — "Latest APK build" badge.
 *
 * Lightweight client-side reader for `/downloads/apk-info.json`. Surfaces
 * the version + build date next to the Android download CTA so visitors
 * can see at a glance that the APK is fresh and maintained.
 *
 * Renders nothing if:
 *   - the JSON manifest is missing (no APK published yet)
 *   - `available: false` in the manifest (placeholder state)
 *   - the fetch errors (network / not-found)
 *
 * The manifest at `/app/frontend/public/downloads/apk-info.json` is
 * version-controlled and updated MANUALLY by whoever publishes the APK
 * — no CI dependency required.
 */
import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 1) return 'today';
    if (diff === 1) return 'yesterday';
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function ApkBuildBadge() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/apk/public-info', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.available) setInfo(data);
      } catch { /* silently hide on any error */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;
  const when = formatDate(info.build_date);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold border border-emerald-200"
      data-testid="apk-build-badge"
      title={`Signed APK · v${info.version || '?'} · built ${info.build_date || 'unknown'}`}
    >
      <CheckCircle2 size={10} />
      v{info.version || '?'}{when ? ` · built ${when}` : ''}
    </span>
  );
}
